import React from 'react'
import { Card, CardContent, Typography, Box, useTheme } from '@material-ui/core'
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Flag as FlagIcon,
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'

interface KPICardProps {
    title: string
    value: string | number
    subtitle?: string
    icon?: 'target' | 'check' | 'warning' | 'error'
    variant?: 'default' | 'success' | 'warning' | 'danger'
}

const useStyles = makeStyles((theme) => ({
    card: {
        borderRadius: theme.shape.borderRadius * 2,
        boxShadow: theme.shadows[4],
        padding: theme.spacing(3),
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: theme.shadows[8],
            transform: 'translateY(-2px)',
        },
    },
    defaultCard: {
        border: `2px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
            borderColor: theme.palette.primary.light,
        },
    },
    successCard: {
        border: `2px solid ${theme.palette.success.light}`,
        backgroundColor: `${theme.palette.success.light}15`,
        '&:hover': {
            borderColor: theme.palette.success.main,
            backgroundColor: `${theme.palette.success.light}25`,
        },
    },
    warningCard: {
        border: `2px solid ${theme.palette.warning.light}`,
        backgroundColor: `${theme.palette.warning.light}15`,
        '&:hover': {
            borderColor: theme.palette.warning.main,
            backgroundColor: `${theme.palette.warning.light}25`,
        },
    },
    dangerCard: {
        border: `2px solid ${theme.palette.error.light}`,
        backgroundColor: `${theme.palette.error.light}15`,
        '&:hover': {
            borderColor: theme.palette.error.main,
            backgroundColor: `${theme.palette.error.light}25`,
        },
    },
    content: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    textContainer: {
        flex: 1,
    },
    title: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.5),
    },
    value: {
        fontSize: '2.5rem',
        fontWeight: 800,
        marginBottom: theme.spacing(0.75),
        lineHeight: 1.1,
    },
    defaultValue: {
        color: theme.palette.text.primary,
    },
    successValue: {
        color: theme.palette.success.main,
    },
    warningValue: {
        color: theme.palette.warning.main,
    },
    dangerValue: {
        color: theme.palette.error.main,
    },
    subtitle: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
        fontWeight: 500,
    },
    trend: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        marginTop: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    positiveTrend: {
        color: theme.palette.success.main,
    },
    negativeTrend: {
        color: theme.palette.error.main,
    },
    iconContainer: {
        padding: theme.spacing(1.5),
        borderRadius: theme.shape.borderRadius * 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: theme.shadows[2],
    },
    defaultIcon: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
    },
    successIcon: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.main,
    },
    warningIcon: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.main,
    },
    dangerIcon: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.main,
    },
    icon: {
        fontSize: '1.5rem',
    },
}))

export const KPICard: React.FC<KPICardProps> = ({
    title,
    value,
    subtitle,
    icon,
    variant = 'default',
}) => {
    const classes = useStyles()
    const theme = useTheme()

    const getCardClass = () => {
        switch (variant) {
            case 'success':
                return classes.successCard
            case 'warning':
                return classes.warningCard
            case 'danger':
                return classes.dangerCard
            default:
                return classes.defaultCard
        }
    }

    const getValueClass = () => {
        switch (variant) {
            case 'success':
                return classes.successValue
            case 'warning':
                return classes.warningValue
            case 'danger':
                return classes.dangerValue
            default:
                return classes.defaultValue
        }
    }

    const getIconContainerClass = () => {
        switch (variant) {
            case 'success':
                return classes.successIcon
            case 'warning':
                return classes.warningIcon
            case 'danger':
                return classes.dangerIcon
            default:
                return classes.defaultIcon
        }
    }

    const getIconComponent = () => {
        if (icon === 'check') return CheckCircleIcon
        if (icon === 'warning') return WarningIcon
        if (icon === 'error') return ErrorIcon
        return FlagIcon // default/target icon
    }

    const IconComponent = getIconComponent()

    return (
        <Card className={`${classes.card} ${getCardClass()}`}>
            <CardContent style={{ padding: 0 }}>
                <Box className={classes.content}>
                    <Box className={classes.textContainer}>
                        <Typography className={classes.title}>
                            {title}
                        </Typography>
                        <Typography
                            className={`${classes.value} ${getValueClass()}`}
                        >
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography className={classes.subtitle}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        className={`${
                            classes.iconContainer
                        } ${getIconContainerClass()}`}
                    >
                        <IconComponent className={classes.icon} />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )
}
